// Pure domain-object → PaletteCandidate mappers. No React, no hooks.
// Each function takes raw API data, a query string, and optional recency
// data, and returns typed PaletteCandidate arrays ready for rankAndCap.
//
// Field weights (plan §Scoring):
//   title ×3, reference/contractNumber ×2, supplierName ×1.5
//
// All URLs are built with URLSearchParams — no manual string concatenation.
//
// Global scope: recordCandidates accepts an optional contractRefById map so
// results can show which contract each record belongs to (e.g.,
// "Deliverable / Obligation · TMRICT25243").

import { matchText, computeScore } from "./scoring";
import { tabSlugForType } from "../manage/tabs/tabRegistry";
import type {
  ManagedContract,
  Contract,
  TrackingItem,
  TabLevelType,
} from "../../types";
import { TAB_LEVEL_TYPES, TRACKING_TYPE_LABELS_SINGULAR } from "../../types";
import type { NavGroup } from "../common/navigationRegistry";
import type { OrgWideInvoice } from "../../types/finance";
import { formatAud } from "../../utils/financeCalc";
import type {
  ManagedContractCandidate,
  DraftContractCandidate,
  RecordCandidate,
  InvoiceCandidate,
  NavCandidate,
  OverflowCandidate,
} from "./paletteTypes";

// Runtime Set of TabLevelType values for the isTabLevelType guard.
const TAB_LEVEL_TYPE_SET = new Set<string>(TAB_LEVEL_TYPES);

/** Type guard: true when type is a TabLevelType (not kpi-measurement/kpi-report). */
function isTabLevelType(type: string): type is TabLevelType {
  return TAB_LEVEL_TYPE_SET.has(type);
}

// ─── Field weight constants ────────────────────────────────────────────

const W_TITLE = 3;
const W_REF = 2;
const W_SUPPLIER = 1.5;

// ─── managedContractCandidates ────────────────────────────────────────

/**
 * Maps managed contracts to palette candidates scored against `query`.
 *
 * @param contracts    The managed-contract list from the register drain
 * @param query        The current palette query string
 * @param lastVisited  Map of contractId → ISO visitedAt string (from recents)
 * @param nowMs        Epoch ms for time-relative scoring (frozen in tests)
 */
export function managedContractCandidates(
  contracts: ManagedContract[],
  query: string,
  lastVisited: Map<string, string> | null,
  nowMs: number = Date.now(),
): ManagedContractCandidate[] {
  const results: ManagedContractCandidate[] = [];

  for (const mc of contracts) {
    // contractNumber/supplierName are optional on reads (sparse stored
    // rows) — an absent field simply can't match.
    const titleMatch = matchText(mc.title, query);
    const refMatch = mc.contractNumber ? matchText(mc.contractNumber, query) : null;
    const supplierMatch = mc.supplierName ? matchText(mc.supplierName, query) : null;

    if (!titleMatch && !refMatch && !supplierMatch) continue;

    // Best field score (max over matched fields)
    const scores: number[] = [];
    const lastVisitedAt = lastVisited?.get(mc.id) ?? null;

    if (titleMatch) {
      scores.push(computeScore(W_TITLE, titleMatch.strength, mc.updatedAt, lastVisitedAt, mc.status, nowMs));
    }
    if (refMatch) {
      scores.push(computeScore(W_REF, refMatch.strength, mc.updatedAt, lastVisitedAt, mc.status, nowMs));
    }
    if (supplierMatch) {
      scores.push(computeScore(W_SUPPLIER, supplierMatch.strength, mc.updatedAt, lastVisitedAt, mc.status, nowMs));
    }

    const score = Math.max(...scores);

    results.push({
      kind: "managed-contract",
      id: `mc:${mc.id}`,
      title: mc.title,
      // Only the present identifier parts — never "undefined · undefined"
      ctx: [mc.contractNumber, mc.supplierName].filter(Boolean).join(" · "),
      url: `/manage/contracts/${encodeURIComponent(mc.id)}`,
      score,
      titleRanges: titleMatch?.ranges,
      contractNumber: mc.contractNumber,
      supplierName: mc.supplierName,
      status: mc.status,
    });
  }

  return results;
}

// ─── draftContractCandidates ──────────────────────────────────────────

/**
 * Maps draft contracts (Contract shape) to palette candidates.
 */
export function draftContractCandidates(
  drafts: Contract[],
  query: string,
  nowMs: number = Date.now(),
): DraftContractCandidate[] {
  const results: DraftContractCandidate[] = [];

  for (const draft of drafts) {
    const titleMatch = matchText(draft.title, query);
    const refMatch = draft.referenceNumber ? matchText(draft.referenceNumber, query) : null;

    if (!titleMatch && !refMatch) continue;

    const scores: number[] = [];
    if (titleMatch) {
      scores.push(computeScore(W_TITLE, titleMatch.strength, draft.updatedAt, null, draft.status, nowMs));
    }
    if (refMatch) {
      scores.push(computeScore(W_REF, refMatch.strength, draft.updatedAt, null, draft.status, nowMs));
    }

    results.push({
      kind: "draft-contract",
      id: `draft:${draft.id}`,
      title: draft.title,
      ctx: draft.referenceNumber,
      url: `/generate/contracts/${encodeURIComponent(draft.id)}`,
      score: Math.max(...scores),
      titleRanges: titleMatch?.ranges,
      referenceNumber: draft.referenceNumber,
      status: draft.status,
    });
  }

  return results;
}

// ─── recordCandidates ────────────────────────────────────────────────

/**
 * Maps tracking items to RecordCandidates. Filters out non-tab-level types
 * (kpi-measurement, kpi-report) which have no standalone tab deep link.
 *
 * URLs are built via tabSlugForType() — sow/variation → ?tab=variations-sows.
 *
 * @param contractRefById Global scope: contractId → contract number (or title).
 *                        When provided, candidates get a "{type label} · {ref}"
 *                        context line so cross-contract results say where they
 *                        live. Contract scope omits it — the contract is implied
 *                        by the search scope.
 */
export function recordCandidates(
  items: TrackingItem[],
  query: string,
  nowMs: number = Date.now(),
  contractRefById?: Map<string, string>,
): RecordCandidate[] {
  const results: RecordCandidate[] = [];

  for (const item of items) {
    // Only tab-level types have a deep-link destination.
    if (!isTabLevelType(item.type)) continue;

    const nameMatch = matchText(item.name, query);
    if (!nameMatch) continue;

    const slug = tabSlugForType(item.type as TabLevelType);
    const params = new URLSearchParams({ tab: slug, item: item.id });
    const url = `/manage/contracts/${encodeURIComponent(item.contractId)}?${params.toString()}`;

    let ctx: string | undefined;
    if (contractRefById) {
      const typeLabel = TRACKING_TYPE_LABELS_SINGULAR[item.type as TabLevelType];
      const ref = contractRefById.get(item.contractId);
      ctx = ref ? `${typeLabel} · ${ref}` : typeLabel;
    }

    results.push({
      kind: "record",
      id: `record:${item.id}`,
      title: item.name,
      ...(ctx !== undefined ? { ctx } : {}),
      url,
      score: computeScore(W_TITLE, nameMatch.strength, item.updatedAt, null, "active", nowMs),
      titleRanges: nameMatch.ranges,
      recordType: item.type as TabLevelType,
      contractId: item.contractId,
    });
  }

  return results;
}

// ─── invoiceCandidates ───────────────────────────────────────────────

/**
 * Maps org-wide slim invoices to InvoiceCandidates. Matches the invoice
 * number (title, highlighted) or any charge label (no title highlight —
 * the matched text isn't the rendered title). Deep link targets the owning
 * contract's finance tab with `?item=` set so FinanceTab reveals the row.
 *
 * @param contractRefById contractId → contract number (or title) for the
 *                        "Invoice · {ref} · {amount}" attribution line.
 */
export function invoiceCandidates(
  invoices: OrgWideInvoice[],
  query: string,
  contractRefById: Map<string, string>,
  nowMs: number = Date.now(),
): InvoiceCandidate[] {
  const results: InvoiceCandidate[] = [];

  for (const invoice of invoices) {
    const numberMatch = matchText(invoice.invoiceNumber, query);
    const labelMatch = numberMatch
      ? null
      : matchText(invoice.chargeLabels.join(" "), query);
    if (!numberMatch && !labelMatch) continue;

    const params = new URLSearchParams({ tab: "finance", item: invoice.id });
    const url = `/manage/contracts/${encodeURIComponent(invoice.contractId)}?${params.toString()}`;

    const ref = contractRefById.get(invoice.contractId);
    const ctx = ["Invoice", ...(ref ? [ref] : []), formatAud(invoice.totalAmount)].join(" · ");

    // invoiceDate stands in for updatedAt in recency scoring — the slim
    // projection carries no updatedAt, and a recent invoice is the one the
    // operator is most likely reaching for.
    results.push({
      kind: "invoice",
      id: `invoice:${invoice.id}`,
      title: invoice.invoiceNumber,
      ctx,
      url,
      score: computeScore(
        W_TITLE,
        (numberMatch ?? labelMatch)!.strength,
        invoice.invoiceDate,
        null,
        "active",
        nowMs,
      ),
      ...(numberMatch ? { titleRanges: numberMatch.ranges } : {}),
      contractId: invoice.contractId,
      status: invoice.status,
    });
  }

  return results;
}

// ─── navCandidates ────────────────────────────────────────────────────

/**
 * Maps pre-gated navigation groups (from buildNavigationGroups) to NavCandidates.
 * Navigation items are always scored as "active" and use a fixed recency of 0
 * (nav items have no updatedAt; they get a neutral recency).
 */
export function navCandidates(
  groups: NavGroup[],
  query: string,
): NavCandidate[] {
  const NEUTRAL_DATE = new Date(0).toISOString();
  const results: NavCandidate[] = [];

  for (const group of groups) {
    for (const item of group.items) {
      const nameMatch = matchText(item.name, query);
      if (!nameMatch) continue;

      results.push({
        kind: "nav",
        id: `nav:${item.href}`,
        title: item.name,
        url: item.href,
        icon: item.icon,
        score: computeScore(W_TITLE, nameMatch.strength, NEUTRAL_DATE, null, "active"),
        titleRanges: nameMatch.ranges,
      });
    }
  }

  return results;
}

// ─── buildOverflowCandidate ──────────────────────────────────────────

type OverflowKind = "managed-contract" | "draft-contract";

/**
 * Builds the "Show all N…" sentinel row for a given section when the
 * result count exceeds the display cap. The URL pre-applies the query
 * as a ?search= parameter (requires ContractRegister + ContractList to
 * honour URL-param initialisation — ContractRegister and ContractList
 * read the ?search= param on mount to prime their filter state).
 */
export function buildOverflowCandidate(
  kind: OverflowKind,
  query: string,
  totalCount: number,
): OverflowCandidate {
  const base = kind === "managed-contract"
    ? "/manage/contracts"
    : "/generate/contracts";
  const params = new URLSearchParams({ search: query });
  const section = kind === "managed-contract" ? "Contract Register" : "Document Drafts";

  return {
    kind: "overflow",
    id: `overflow:${kind}`,
    label: `Show all ${totalCount} matches in ${section}`,
    url: `${base}?${params.toString()}`,
    score: 0,
  };
}
